import React, { useState } from 'react';
import VersionBadge from './VersionBadge';
import ReleaseNotes from './ReleaseNotes';
import { formatFileSize, formatDate, formatDownloadCount } from '../../utils/formatters';

const VersionCard = ({
  version,
  onDownload,
  showDownloadButton = true,
  compact = false
}) => {
  const [downloading, setDownloading] = useState(false);

  if (!version) {
    return null;
  }

  const handleDownload = async () => {
    if (onDownload && !downloading) {
      setDownloading(true);
      try {
        await onDownload(version);
      } catch (error) {
        console.error('Download error:', error);
      } finally {
        // Keep downloading state for a moment to show feedback
        setTimeout(() => {
          setDownloading(false);
        }, 1000);
      }
    }
  };

  const fileSize = version.file_size || version.formatted_size;
  const displayFileSize = typeof fileSize === 'number'
    ? formatFileSize(fileSize)
    : fileSize || 'Unknown';

  const releaseDate = version.released_at || version.created_at;
  const displayDate = formatDate(releaseDate, 'short');

  const downloadCount = version.downloads_count || 0;
  const displayDownloads = formatDownloadCount(downloadCount);

  return (
    <div
      style={{
        ...styles.card,
        ...(compact ? styles.cardCompact : {})
      }}
    >
      {/* Header Section */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h3 style={styles.versionNumber}>
            Version {version.version}
            {version.version_code && (
              <span style={styles.versionCode}>({version.version_code})</span>
            )}
          </h3>
          <VersionBadge
            isLatest={version.is_latest}
            releaseType={version.release_type}
            isActive={version.is_active}
          />
        </div>

        {showDownloadButton && version.is_active && (
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="version-download-btn"
            style={{
              ...(downloading ? styles.downloadButtonDisabled : {})
            }}
          >
            {downloading ? (
              <>
                <span style={styles.downloadingSpinner}>⟳</span>
                Downloading...
              </>
            ) : (
              <>
                <span style={styles.downloadIcon}>⬇</span>
                Download
              </>
            )}
          </button>
        )}
      </div>

      {/* Info Section */}
      <div style={styles.infoSection}>
        <div style={styles.infoItem}>
          <span style={styles.infoIcon}>📅</span>
          <span style={styles.infoLabel}>Released:</span>
          <span style={styles.infoValue}>{displayDate}</span>
        </div>

        <div style={styles.infoItem}>
          <span style={styles.infoIcon}>📦</span>
          <span style={styles.infoLabel}>Size:</span>
          <span style={styles.infoValue}>{displayFileSize}</span>
        </div>

        {downloadCount > 0 && (
          <div style={styles.infoItem}>
            <span style={styles.infoIcon}>⬇️</span>
            <span style={styles.infoLabel}>Downloads:</span>
            <span style={styles.infoValue}>{displayDownloads}</span>
          </div>
        )}
      </div>

      {/* SDK Requirements */}
      {(version.min_sdk || version.target_sdk) && !compact && (
        <div style={styles.sdkSection}>
          {version.min_sdk && (
            <span style={styles.sdkBadge}>
              Min SDK: {version.min_sdk}
            </span>
          )}
          {version.target_sdk && (
            <span style={styles.sdkBadge}>
              Target SDK: {version.target_sdk}
            </span>
          )}
        </div>
      )}

      {/* Release Notes Section */}
      {version.release_notes && (
        <div style={styles.notesSection}>
          <h4 style={styles.notesTitle}>What's New</h4>
          <ReleaseNotes
            notes={version.release_notes}
            maxLength={compact ? 100 : 200}
          />
        </div>
      )}

      {/* Additional Info */}
      {!compact && (version.contains_ads || version.in_app_purchases) && (
        <div style={styles.additionalInfo}>
          {version.contains_ads && (
            <span style={styles.infoBadge}>
              📢 Contains Ads
            </span>
          )}
          {version.in_app_purchases && (
            <span style={styles.infoBadge}>
              💳 In-App Purchases
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  card: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
    border: '1px solid #e0e0e0',
    marginBottom: '16px'
  },
  cardCompact: {
    padding: '16px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '12px'
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1
  },
  versionNumber: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#2c3e50',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
  },
  versionCode: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#7f8c8d',
    marginLeft: '4px'
  },
  downloadButton: {
    backgroundColor: '#3498db',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
  },
  downloadButtonDisabled: {
    backgroundColor: '#95a5a6',
    cursor: 'not-allowed',
    opacity: 0.7
  },
  downloadIcon: {
    fontSize: '16px'
  },
  downloadingSpinner: {
    fontSize: '16px',
    display: 'inline-block',
    animation: 'spin 1s linear infinite'
  },
  infoSection: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '16px',
    paddingBottom: '16px',
    borderBottom: '1px solid #ecf0f1'
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px'
  },
  infoIcon: {
    fontSize: '16px'
  },
  infoLabel: {
    color: '#7f8c8d',
    fontWeight: '500'
  },
  infoValue: {
    color: '#2c3e50',
    fontWeight: '600'
  },
  sdkSection: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap'
  },
  sdkBadge: {
    backgroundColor: '#ecf0f1',
    color: '#34495e',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600'
  },
  notesSection: {
    marginTop: '16px'
  },
  notesTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: '8px',
    marginTop: 0
  },
  additionalInfo: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #ecf0f1',
    flexWrap: 'wrap'
  },
  infoBadge: {
    backgroundColor: '#fff3cd',
    color: '#856404',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    border: '1px solid #ffeaa7'
  }
};

export default VersionCard;
